import { useState } from "react";
import { Button, Menu } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  toCsv,
  toJson,
  downloadBlob,
  exportFilename,
  type ExportField,
} from "../../lib/export";
import { errorMessage } from "../../lib/axios";

// Generic CSV/JSON export button. `fetchAll` returns every row to export
// (typically via collectAll over the page's list API + active filters).
export function ExportMenu<T>({
  fetchAll,
  fields,
  filename,
  disabled,
}: {
  fetchAll: () => Promise<T[]>;
  fields: ExportField<T>[];
  filename: string;
  disabled?: boolean;
}) {
  const [loading, setLoading] = useState(false);

  async function run(format: "csv" | "json") {
    setLoading(true);
    try {
      const rows = await fetchAll();
      if (!rows.length) {
        notifications.show({ color: "yellow", message: "Nothing to export" });
        return;
      }
      if (format === "csv") {
        downloadBlob(
          exportFilename(filename, "csv"),
          toCsv(rows, fields),
          "text/csv",
        );
      } else {
        downloadBlob(
          exportFilename(filename, "json"),
          toJson(rows, fields),
          "application/json",
        );
      }
      notifications.show({ color: "green", message: `Exported ${rows.length} rows` });
    } catch (err) {
      notifications.show({
        color: "red",
        title: "Export failed",
        message: errorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Menu shadow="md" position="bottom-end" withinPortal>
      <Menu.Target>
        <Button
          variant="default"
          leftSection={<IconDownload size={16} />}
          loading={loading}
          disabled={disabled}
        >
          Export
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => run("csv")}>Export CSV</Menu.Item>
        <Menu.Item onClick={() => run("json")}>Export JSON</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
